// Treasury → Revenue (overview) — the money the fee policies bring in.
//
// Reads from settlement-service via the backoffice proxy. Only fees
// that actually landed on the platform wallet count (committed
// credits); pending and failed credits are chased through the
// reconciliation console, not here.
//
// This is GROSS fee revenue — the platform's share of it, after admin
// commission is carved out, lives on the Commissions page.
//
// Layout mirrors the Fees pages it sits next to: page header, three
// summary tiles, then the daily series as a chart for shape and a
// table for exact, copy-pasteable numbers (finance asked for the
// table; the chart is the at-a-glance layer above it).
//
// ── one currency at a time (SM-495) ──────────────────────────────────
//
// Every figure on this page belongs to the ONE currency the picker
// names — GOLD until an operator changes it, which is what the page has
// always reported. There is deliberately no "All currencies": a total
// over RED, GREEN and GOLD would be a sum of minor units that share a
// scale but not a value, and it would be wrong in a way nothing on
// screen could reveal (G8). What replaces it is the strip below the
// tiles, which shows each currency's own total side by side, labelled,
// never added.
//
// The currency lives in the URL alongside the day window, because a
// filtered view of this page is something an operator pastes into a
// finance ticket: it has to survive a reload and the back button.
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  fetchRevenueDaily,
  fetchRevenueSummary,
  fetchSystemAccounts,
  keys,
  revenueKeys,
  type RevenueCurrencyTotals,
} from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Skeleton } from "@/components/ui/Skeleton";
import { CurrencyAmount, CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { RevenueChart } from "@/components/fees/RevenueChart";
import { LoadFailed } from "@/components/fees/LoadFailed";
import {
  AllocateRevenueDialog,
  PLATFORM_REVENUE_CODE,
  findPlatformRevenue,
} from "@/components/treasury/AllocateRevenueDialog";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";
import { cn } from "@/lib/cn";
import { useCurrencyName } from "@/components/currency/CurrencyPresentationProvider";
import {
  DEFAULT_REVENUE_CURRENCY,
  RevenueCurrencyFilter,
  parseRevenueCurrency,
} from "@/components/currency/RevenueCurrencyFilter";
import type { CurrencyCode } from "@/lib/currency";

interface RevenueSearch {
  currency?: CurrencyCode;
  days?: string;
}

export const Route = createFileRoute("/_app/treasury/revenue/")({
  validateSearch: (raw: Record<string, unknown>): RevenueSearch => ({
    currency: parseRevenueCurrency(raw.currency),
    days: WINDOW_OPTIONS.some((o) => o.value === raw.days)
      ? (raw.days as string)
      : undefined,
  }),
  // The loader prefetches the DEFAULT view only. It runs before the
  // component, and warming a currency the URL did not ask for would
  // cost a request nobody reads; the component's own query covers the
  // rest.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: revenueKeys.summary(DEFAULT_REVENUE_CURRENCY),
      queryFn: () => fetchRevenueSummary(DEFAULT_REVENUE_CURRENCY),
    }),
  component: RevenuePage,
});

const WINDOW_OPTIONS = [
  { value: "7", label: "7d" },
  { value: "30", label: "30d" },
  { value: "90", label: "90d" },
  { value: "365", label: "1y" },
];

function RevenuePage() {
  const search = useSearch({ from: "/_app/treasury/revenue/" });
  const navigate = useNavigate();
  const currency = search.currency ?? DEFAULT_REVENUE_CURRENCY;
  const days = search.days ?? "30";
  const setSearch = (next: Partial<RevenueSearch>) =>
    void navigate({ to: ".", search: (prev) => ({ ...prev, ...next }), replace: true });

  const summaryQ = useQuery({
    queryKey: revenueKeys.summary(currency),
    queryFn: () => fetchRevenueSummary(currency),
  });
  const dailyQ = useQuery({
    queryKey: revenueKeys.daily(Number(days), currency),
    queryFn: () => fetchRevenueDaily(Number(days), currency),
  });
  const rows = dailyQ.data ?? [];
  // The server echoes the currency it aggregated. Trusting the echo over
  // the request is what keeps a label honest if the two ever disagree;
  // an older settlement that omits it aggregated GOLD, which is the only
  // currency it knew how to report.
  const shown = parseRevenueCurrency(summaryQ.data?.currency) ?? currency;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Revenue</h1>
        <p className="max-w-[75ch] text-sm text-muted">
          Gross fees collected by the platform, in one currency at a time.
          Only fees that actually landed on the platform wallet are counted;
          adjust the rates on the{" "}
          <Link
            to="/treasury/fees/policies"
            className="text-accent hover:underline"
          >
            Fee policies
          </Link>{" "}
          page. The share of these fees paid out to admins is broken down
          under{" "}
          <Link
            to="/treasury/revenue/commissions"
            // Hand the Commissions report the currency this page is scoped
            // to, not the report's own default. The two pages read the same
            // fees from opposite ends; landing on GOLD from a RED revenue
            // view is how an operator concludes the commission side is empty.
            search={{ currency }}
            className="text-accent hover:underline"
          >
            Commissions
          </Link>
          .
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <RevenueCurrencyFilter
          className="w-56"
          value={currency}
          onChange={(next) => setSearch({ currency: next })}
        />
        <p className="pb-2 text-2xs text-t-4">
          Every figure below is in this currency. Revenue is never totalled
          across currencies — each one is reported on its own.
        </p>
      </div>

      {/* The console can deploy ahead of settlement. An older settlement
          ignores the filter and answers in GOLD regardless, and the
          figures below are labelled with what it actually aggregated —
          so without this line the page would show gold coins under a RED
          selection and look broken rather than out of date. */}
      {shown !== currency && !summaryQ.isLoading && (
        <p className="text-2xs text-warning">
          This deployment reports Gold only. The figures below are Gold, not the
          currency selected above — the settlement service has to be updated
          before it can report the others.
        </p>
      )}

      {summaryQ.isError ? (
        <LoadFailed
          what="the revenue summary"
          error={summaryQ.error}
          onRetry={() => void summaryQ.refetch()}
        />
      ) : (
      <div className="grid gap-4 sm:grid-cols-3">
        <SummaryCard
          label="Today"
          currency={shown}
          amount={summaryQ.data?.today_amount}
          fills={summaryQ.data?.today_fills}
          makerAmount={summaryQ.data?.today_maker_amount}
          takerAmount={summaryQ.data?.today_taker_amount}
          redemptionAmount={summaryQ.data?.today_redemption_amount}
          loading={summaryQ.isLoading}
        />
        <SummaryCard
          label="Month to date"
          currency={shown}
          amount={summaryQ.data?.mtd_amount}
          fills={summaryQ.data?.mtd_fills}
          makerAmount={summaryQ.data?.mtd_maker_amount}
          takerAmount={summaryQ.data?.mtd_taker_amount}
          redemptionAmount={summaryQ.data?.mtd_redemption_amount}
          loading={summaryQ.isLoading}
        />
        <SummaryCard
          label="All time"
          currency={shown}
          amount={summaryQ.data?.total_amount}
          fills={summaryQ.data?.total_fills}
          makerAmount={summaryQ.data?.total_maker_amount}
          takerAmount={summaryQ.data?.total_taker_amount}
          redemptionAmount={summaryQ.data?.total_redemption_amount}
          loading={summaryQ.isLoading}
          accent
        />
      </div>
      )}

      <CurrencyTotalsStrip
        rows={summaryQ.data?.by_currency}
        selected={shown}
        onSelect={(next) => setSearch({ currency: next })}
      />

      <Card>
        <CardHeader>
          <CardTitle>Daily revenue</CardTitle>
          <CurrencyBadge currency={shown} />
          <SegmentedControl
            options={WINDOW_OPTIONS}
            value={days}
            onChange={(v) => setSearch({ days: v })}
          />
        </CardHeader>
        <CardBody className="p-0">
          {dailyQ.isLoading ? (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-8" />
              ))}
            </div>
          ) : dailyQ.isError ? (
            <div className="p-5">
              <LoadFailed
                what="daily revenue"
                error={dailyQ.error}
                onRetry={() => void dailyQ.refetch()}
              />
            </div>
          ) : rows.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-muted">
              No fee revenue in this window for the currency selected above.
              Another currency may still have some — the totals under the tiles
              say which.
            </p>
          ) : (
            <>
              <div className="border-b border-border/60 px-5 pb-2 pt-4">
                <RevenueChart rows={rows} currency={shown} />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-surface-2">
                    <tr>
                      <th className="px-5 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">
                        Day (UTC)
                      </th>
                      <th className="px-3 py-3 text-right text-2xs font-medium uppercase tracking-wide text-muted">
                        Fills
                      </th>
                      <th className="px-3 py-3 text-right text-2xs font-medium uppercase tracking-wide text-muted">
                        Maker
                      </th>
                      <th className="px-3 py-3 text-right text-2xs font-medium uppercase tracking-wide text-muted">
                        Taker
                      </th>
                      <th className="px-3 py-3 text-right text-2xs font-medium uppercase tracking-wide text-muted">
                        Redeem
                      </th>
                      <th className="px-5 py-3 text-right text-2xs font-medium uppercase tracking-wide text-muted">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.day}
                        className="border-b border-border/60 last:border-0 hover:bg-surface-2/60"
                      >
                        <td className="px-5 py-2.5 font-mono text-xs text-t-2">
                          {row.day}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-xs text-muted">
                          {row.fills.toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-xs text-t-2">
                          <CurrencyAmount currency={row.currency ?? shown} value={row.maker_amount} />
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-xs text-t-2">
                          <CurrencyAmount currency={row.currency ?? shown} value={row.taker_amount} />
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-xs text-t-2">
                          {row.redemption_amount === undefined ? (
                            <span className="text-muted">—</span>
                          ) : (
                            <CurrencyAmount
                              currency={row.currency ?? shown}
                              value={row.redemption_amount}
                            />
                          )}
                        </td>
                        <td className="px-5 py-2.5 text-right font-mono text-sm text-fg">
                          <CurrencyAmount currency={row.currency ?? shown} value={row.amount} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <PlatformAccountHint currency={shown} />
    </div>
  );
}

// CurrencyTotalsStrip — every currency's all-time fee revenue, side by
// side, each labelled with its own mark and NEVER added up.
//
// This is the concrete form of the rule the picker implies: there is no
// cross-currency total on this page, because RED, GREEN and GOLD share
// a wire scale but not a value (G8). Adding them would produce a figure
// that looks authoritative and means nothing.
//
// It also answers the question the picker alone cannot: which
// currencies have any revenue at all. Without it an operator has to
// select each of the three in turn to find out, and two of those trips
// end at an empty page indistinguishable from a broken one. The
// server's by_currency block is the whole picture regardless of the
// filter, so the strip does not change as the selection does — only
// which entry is marked as selected.
//
// Absent on a settlement that predates the block: rendered as nothing
// rather than as three zeros, because "this deployment cannot say" and
// "these currencies earned nothing" are different claims.
function CurrencyTotalsStrip({
  rows,
  selected,
  onSelect,
}: {
  rows: RevenueCurrencyTotals[] | undefined;
  selected: CurrencyCode;
  onSelect: (next: CurrencyCode) => void;
}) {
  if (!rows?.length) return null;
  return (
    <Card>
      <CardBody className="space-y-2">
        <div className="text-2xs font-semibold uppercase tracking-wide text-t-3">
          All-time revenue by currency
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          {rows.map((row) => (
            <CurrencyTotal
              key={row.currency}
              row={row}
              selected={row.currency === selected}
              onSelect={onSelect}
            />
          ))}
        </div>
        <p className="text-2xs text-t-4">
          Each currency is reported on its own. These figures are never added
          together — the three share a wire scale but not a value.
        </p>
      </CardBody>
    </Card>
  );
}

// One entry. A button rather than a plain figure: the whole point of
// seeing the three totals is to go and look at one of them, and the
// picker above is a long way up the page by the time an operator has
// read the tiles.
function CurrencyTotal({
  row,
  selected,
  onSelect,
}: {
  row: RevenueCurrencyTotals;
  selected: boolean;
  onSelect: (next: CurrencyCode) => void;
}) {
  const code = parseRevenueCurrency(row.currency);
  const name = useCurrencyName(row.currency);
  const body = (
    <>
      <div className="text-2xs text-t-3">{name}</div>
      <div className="font-display text-lg tracking-tight text-t-1">
        <CurrencyAmount currency={row.currency} value={row.total_amount} />
      </div>
      <div className="text-2xs text-t-4">
        {`${(row.total_fills ?? 0).toLocaleString()} fills`}
      </div>
    </>
  );
  // A code the console cannot render is shown but not made clickable —
  // selecting it would put a currency in the URL that the picker cannot
  // represent. That should not happen; the registry holds exactly three
  // codes. It is here because a fourth would otherwise be a dead click.
  if (!code) {
    return <div className="rounded-md border border-line-1 px-3 py-2">{body}</div>;
  }
  return (
    <button
      type="button"
      onClick={() => onSelect(code)}
      aria-pressed={selected}
      className={cn(
        "rounded-md border px-3 py-2 text-left transition-colors",
        selected
          ? "border-neon/60 bg-neon/10"
          : "border-line-1 hover:border-line-3",
      )}
    >
      {body}
    </button>
  );
}

function SummaryCard({
  label,
  currency,
  amount,
  fills,
  makerAmount,
  takerAmount,
  redemptionAmount,
  loading,
  accent,
}: {
  label: string;
  /** The one currency every figure on this card is in (SM-495). */
  currency: CurrencyCode;
  amount: string | undefined;
  fills: number | undefined;
  makerAmount?: string;
  takerAmount?: string;
  // Redemption fees (SM-327). Undefined when the API predates it —
  // rendered as "—" rather than a zero, because a zero here would claim
  // the platform collected no redemption fees when the truth is that
  // this deployment cannot say.
  redemptionAmount?: string;
  loading: boolean;
  accent?: boolean;
}) {
  return (
    <Card>
      <CardBody className="space-y-1.5">
        <div className="text-2xs font-semibold uppercase tracking-wide text-t-3">
          {label}
        </div>
        {loading ? (
          <Skeleton className="h-9 w-28" />
        ) : (
          <div
            className={`font-display text-3xl tracking-tight ${
              accent ? "text-fg" : "text-t-1"
            }`}
          >
            <CurrencyAmount currency={currency} value={amount} />
          </div>
        )}
        <div className="text-2xs text-muted">
          {loading ? (
            <Skeleton className="inline-block h-3 w-12" />
          ) : (
            `${(fills ?? 0).toLocaleString()} fills`
          )}
        </div>
        {/* Maker / Taker / Redemption split. Under the seeded
            Polymarket convention (maker 0, taker > 0) trading revenue
            lands in Taker; surfaced anyway so the split isn't invisible
            once maker rates go non-zero somewhere.

            The three parts must add up to the total above them. That is
            the whole reason Redemption is here: Total is a SUM over
            every fee role, so once a redemption fee is collected, Maker
            + Taker silently stops equalling it. */}
        {!loading && (makerAmount !== undefined || takerAmount !== undefined) && (
          <div className="pt-1 text-2xs text-t-3">
            <span className="font-medium text-t-2">Maker</span>{" "}
            <CurrencyAmount currency={currency} value={makerAmount} />
            <span className="mx-1.5 text-border">·</span>
            <span className="font-medium text-t-2">Taker</span>{" "}
            <CurrencyAmount currency={currency} value={takerAmount} />
            <span className="mx-1.5 text-border">·</span>
            <span className="font-medium text-t-2">Redeem</span>{" "}
            {redemptionAmount === undefined ? (
              <span className="text-muted">—</span>
            ) : (
              <CurrencyAmount currency={currency} value={redemptionAmount} />
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// PlatformAccountHint — the earn→allocate bridge between this page
// (settlement's view of fee revenue) and Treasury → System Accounts
// (the ledger balance the fees land on). With SYSTEM_ACCOUNT_VIEW it
// shows the live PLATFORM_REVENUE balance, a link to the account's
// movement history, and (perm-gated) the Allocate action; without the
// perm — or against a ledger predating SM-152 — it degrades to the
// static explainer.
function PlatformAccountHint({ currency }: { currency: CurrencyCode }) {
  const perms = useMyPerms();
  const canView = perms.loaded && (perms.isSuperAdmin || perms.has(Perm.SystemAccountView));
  const [allocating, setAllocating] = useState(false);
  const accountsQ = useQuery({
    queryKey: keys.systemAccounts(),
    queryFn: fetchSystemAccounts,
    enabled: canView,
  });
  const accounts = accountsQ.data ?? [];
  // The row for THIS currency, not the first row that shares the code:
  // ledger keeps one PLATFORM_REVENUE row per currency (migration 0025),
  // and a balance beside a currency-scoped total has to be that
  // currency's or the two figures silently describe different money.
  const revenue = findPlatformRevenue(accounts, currency);
  const canAllocate =
    perms.isSuperAdmin || perms.has(Perm.SystemAccountAllocateRevenue);

  return (
    <Card>
      <CardBody className="space-y-2 text-xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-2xs font-semibold uppercase tracking-wide text-t-3">
            Where this money sits
          </div>
          {revenue && (
            <div className="flex items-center gap-3">
              <span className="text-2xs text-t-3">
                Balance{" "}
                <span className="font-display text-base tracking-tight text-fg">
                  <CurrencyAmount currency={currency} value={revenue.available} />
                </span>
              </span>
              {canAllocate && (
                <Button size="sm" onClick={() => setAllocating(true)}>
                  Allocate revenue
                </Button>
              )}
            </div>
          )}
        </div>
        <p className="max-w-[75ch] leading-relaxed text-t-2">
          Fees deducted at settlement are credited to the platform&apos;s own
          revenue wallet in the ledger. From there, finance allocates budget
          into system accounts (promotions, market making, rewards) — every
          move is an explicit, audited ledger transfer.
        </p>
        <p className="font-mono text-2xs text-t-4">
          user_id=_platform · wallet_id=platform-revenue-credit
          {revenue && (
            <>
              {" · "}
              <Link
                to="/treasury/accounts/$code"
                params={{ code: PLATFORM_REVENUE_CODE }}
                search={{ currency }}
                className="text-accent hover:underline"
              >
                view account &amp; movements →
              </Link>
            </>
          )}
        </p>
      </CardBody>
      <AllocateRevenueDialog
        open={allocating}
        accounts={accounts}
        currency={currency}
        onClose={() => setAllocating(false)}
      />
    </Card>
  );
}
